import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import PagePremission from '@/routes/Restrictions/PagePremission';
import { lazyRouteComponent, preloadComponent } from '@/utils/lazyLoad';
import Premission from '@/utils/premissions/premission.type';

// Optimized lazy loading with performance enhancements

const AreasDistributionPage = lazyRouteComponent(
  () => import('@/pages/AreasContainer'),
);

const ClientsPage = lazyRouteComponent(() => import('@/pages/Clients'));

const LicenseReviewPage = lazyRouteComponent(
  () => import('@/pages/Clients/LicenseReview'),
);

const ClientBalanceStatementPage = lazyRouteComponent(
  () => import('@/pages/ClientBalanceStatement'),
);

const ClientLocationsPage = lazyRouteComponent(
  () => import('@/pages/ClientLocations'),
);

const InvoicesByMonthPage = lazyRouteComponent(
  () => import('@/pages/InvoicesByMonth'),
);

// Preload the clients page since it's the default route
preloadComponent(() => import('@/pages/Clients'));

// Define routes with optimized components
const SettingRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="/settings/clients" replace />,
  },

  {
    path: 'areas',
    element: (
      <PagePremission prem={Premission.Prem_Area_Container_View}>
        <AreasDistributionPage />
      </PagePremission>
    ),
  },
  {
    path: 'clients',
    element: (
      <PagePremission prem={Premission.Prem_Clients_Main}>
        <ClientsPage />
      </PagePremission>
    ),
  },
  {
    path: 'clients/license-review',
    element: (
      <PagePremission prem={Premission.Prem_Clients_License_Approve}>
        <LicenseReviewPage />
      </PagePremission>
    ),
  },
  {
    path: 'clients/balance-statement',
    element: (
      <PagePremission prem={Premission.Prem_Clients_Balances_List}>
        <ClientBalanceStatementPage />
      </PagePremission>
    ),
  },
  {
    path: 'locations',
    element: (
      <PagePremission prem={Premission.Prem_Location_Add}>
        <ClientLocationsPage />
      </PagePremission>
    ),
  },
  {
    path: 'invoices-by-month',
    element: (
      <PagePremission prem={Premission.Prem_Clients_Main}>
        <InvoicesByMonthPage />
      </PagePremission>
    ),
  },
  // {
  //   path: 'lists',
  //   element: (
  //     <PagePremission prem={Premission.Prem_Employee_Emp_Lists}>
  //       <ClientListsPage />
  //     </PagePremission>
  //   ),
  // },
];

// Advanced preloading API for context-aware prefetching
export const prefetchSettingsRoutes = {
  prefetchClients: () => void import('@/pages/Clients'),
  prefetchLicenseReview: () => void import('@/pages/Clients/LicenseReview'),
  prefetchBalanceStatement: () => void import('@/pages/ClientBalanceStatement'),
  prefetchDistribution: () => void import('@/pages/AreasContainer'),
  prefetchLists: () => void import('@/pages/ClientList'),
  prefetchLocations: () => void import('@/pages/ClientLocations'),
  prefetchInvoicesByMonth: () => void import('@/pages/InvoicesByMonth'),
  prefetchAllSettings: () => {
    void import('@/pages/Clients');
    void import('@/pages/Clients/LicenseReview');
    void import('@/pages/ClientBalanceStatement');
    void import('@/pages/AreasContainer');
    void import('@/pages/ClientList');
    void import('@/pages/ClientLocations');
    void import('@/pages/InvoicesByMonth');
  },
};

export default SettingRoutes;
